<template>
  <div :key="componentKey" data-testid="operatingcostinvoices-detail">
    <!-- Toolbar wie bei ContactpersonsDetail – nur in Modal ausgeblendet -->
    <DetailToolbar
      v-if="!inModal"
      :isNew="isNew"
      :editMode="editMode"
      :isAnyFieldDirty="isAnyFieldDirty"
      :onGoToList="goToList"
      :onToggleEditMode="toggleEditMode"
      :onConfirmDelete="confirmDelete"
      :headerText="
        editData?.issuerLegalName ?? t('pages.operatingcostinvoices.headlineUNewUtilitybill')
      "
    />
    <form>
      <h3 class="pt-8">{{ t('text.masterData') }}</h3>
      <SectionWrapper :class="editMode ? 'editmode' : 'viewmode'">
        <div v-if="editData.ticketId" class="flex">
          <LinkedTicketTag :ticketId="editData.ticketId" />
        </div>

        <div :class="isDisabled('pointOfSaleId') ? 'viewmode' : 'flex'">
          <FormLazySelect
            :key="`contractId`"
            v-model="contractId"
            :data-testid="`contractId-select`"
            :disabled="isDisabled('contractId')"
            :fetchPagedData="fetchSimpleContracts"
            :fetchSingleItem="fetchContractDetails"
            :initialValue="editData.contractId || undefined"
            :isDirty="handleDirtyState('contractId')"
            :name="`contractId`"
            :viewMode="isDisabled('contractId')"
            filter
            label="selectContract"
            showClear
          />
          <FormLazySelect
            v-if="hasSelectedContract"
            :key="`pointOfSale-${contractId}`"
            v-model="pointOfSaleId"
            name="pointOfSaleId"
            label="pos"
            :fetchPagedData="fetchSimplePointOfSales"
            :fetchSingleItem="fetchPointOfSaleDetails"
            :initialValue="editData.pointOfSaleId || undefined"
            :disabled="isDisabled('pointOfSaleId')"
            :isDirty="handleDirtyState('pointOfSaleId')"
            :viewMode="isDisabled('pointOfSaleId')"
            showClear
            filter
          />
        </div>

        <div v-if="hasSelectedContract" class="flex">
          <FormInputText
            v-model="issuerLegalName"
            name="issuerLegalName"
            :placeholder="ph('issuerLegalName')"
            :isDirty="handleDirtyState('issuerLegalName')"
            :initialValue="editData.issuerLegalName"
            :disabled="isDisabled('issuerLegalName')"
            :suggestions="props.suggestions?.issuerLegalName"
            :acceptPlaceholderOnDblClick="true"
          />

          <FormDatePicker
            v-model="issueDate"
            name="issueDate"
            :placeholder="ph('issueDate')"
            :isDirty="handleDirtyState('issueDate')"
            :initialValue="editData.issueDate"
            :disabled="isDisabled('issueDate')"
            filter
            :acceptPlaceholderOnDblClick="true"
          />
        </div>

        <div v-if="hasSelectedContract" class="flex">
          <FormDatePicker
            v-model="periodStart"
            name="periodStart"
            :placeholder="ph('periodStart')"
            :isDirty="handleDirtyState('periodStart')"
            :initialValue="editData.periodStart"
            :disabled="isDisabled('periodStart')"
            filter
            :acceptPlaceholderOnDblClick="true"
          />
          <FormDatePicker
            v-model="periodEnd"
            name="periodEnd"
            :placeholder="ph('periodEnd')"
            :isDirty="handleDirtyState('periodEnd')"
            :initialValue="editData.periodEnd"
            :disabled="isDisabled('periodEnd')"
            filter
            :acceptPlaceholderOnDblClick="true"
          />
        </div>
      </SectionWrapper>

      <h3 v-if="hasSelectedContract" class="pt-8">
        {{ t('text.deadlinesAndPayments') }}
      </h3>

      <SectionWrapper v-if="hasSelectedContract" :class="editMode ? 'editmode' : 'viewmode'">
        <div class="flex">
          <FormDatePicker
            v-model="documentReviewDeadline"
            name="documentReviewDeadline"
            :placeholder="ph('documentReviewDeadline')"
            :isDirty="handleDirtyState('documentReviewDeadline')"
            :initialValue="editData.documentReviewDeadline"
            :disabled="isDisabled('documentReviewDeadline')"
            filter
            :acceptPlaceholderOnDblClick="true"
          />
          <FormDatePicker
            v-model="objectionDeadline"
            name="objectionDeadline"
            :placeholder="ph('objectionDeadline')"
            :isDirty="handleDirtyState('objectionDeadline')"
            :initialValue="editData.objectionDeadline"
            :disabled="isDisabled('objectionDeadline')"
            filter
            :acceptPlaceholderOnDblClick="true"
          />
          <FormDatePicker
            v-model="internalObjectionDeadline"
            name="internalObjectionDeadline"
            :placeholder="ph('internalObjectionDeadline')"
            :isDirty="handleDirtyState('internalObjectionDeadline')"
            :initialValue="editData.internalObjectionDeadline"
            :disabled="isDisabled('internalObjectionDeadline')"
            filter
            :acceptPlaceholderOnDblClick="true"
          />
        </div>

        <div class="flex">
          <FormCheckbox
            v-model="isHeatingCostsCompliantHKVO"
            name="isHeatingCostsCompliantHKVO"
            :placeholder="ph('isHeatingCostsCompliantHKVO')"
            :isDirty="handleDirtyState('isHeatingCostsCompliantHKVO')"
            :initialValue="editData.isHeatingCostsCompliantHKVO"
            :disabled="isDisabled('isHeatingCostsCompliantHKVO')"
          />
          <FormInputNumber
            v-model="costsNet"
            name="costsNet"
            :isDirty="handleDirtyState('costsNet')"
            :initialValue="editData.costsNet"
            :disabled="isDisabled('costsNet')"
            :currency="currency"
            formatType="currency"
          />
          <!-- :suggestions="props.suggestions?.costsNet" -->
          <FormInputNumber
            v-model="vatRate"
            name="vatRate"
            :placeholder="ph('vatRate')"
            :isDirty="handleDirtyState('vatRate')"
            :initialValue="editData.vatRate"
            :disabled="isDisabled('vatRate')"
            :suffix="`%`"
          />
          <!-- :suggestions="props.suggestions?.vatRate" -->
        </div>

        <div class="flex">
          <FormInputNumber
            v-model="customerPrepaymentNet"
            name="customerPrepaymentNet"
            :isDirty="handleDirtyState('customerPrepaymentNet')"
            :initialValue="editData.customerPrepaymentNet"
            :disabled="isDisabled('customerPrepaymentNet')"
            formatType="currency"
            :currency="currency"
          />
          <!-- :suggestions="props.suggestions?.customerPrepaymentNet" -->
          <FormInputNumber
            v-model="customerPrepaymentGross"
            name="customerPrepaymentGross"
            :isDirty="handleDirtyState('customerPrepaymentGross')"
            :initialValue="editData.customerPrepaymentGross"
            :disabled="isDisabled('customerPrepaymentGross')"
            formatType="currency"
            :currency="currency"
          />
          <FormSelect
            v-model="currency"
            name="currency"
            label="currency"
            :options="Object.values(OperatingCostInvoiceV1Dto.currency)"
            :isDirty="handleDirtyState('currency')"
            :initialValue="editData?.currency"
            :disabled="!editMode"
            untranslated
          />
          <!-- :suggestions="props.suggestions?.customerPrepaymentGross" -->
        </div>

        <div class="flex">
          <FormInputNumber
            v-model="statedCustomerPrepaymentNet"
            name="statedCustomerPrepaymentNet"
            :isDirty="handleDirtyState('statedCustomerPrepaymentNet')"
            :initialValue="editData.statedCustomerPrepaymentNet"
            :disabled="isDisabled('statedCustomerPrepaymentNet')"
            formatType="currency"
            :currency="currency"
          />
          <!-- :suggestions="props.suggestions?.statedCustomerPrepaymentNet" -->
          <FormInputNumber
            v-model="settlementResultNet"
            name="settlementResultNet"
            :isDirty="handleDirtyState('settlementResultNet')"
            :initialValue="editData.settlementResultNet"
            :disabled="isDisabled('settlementResultNet')"
            formatType="currency"
            :currency="currency"
          />
          <!-- :suggestions="props.suggestions?.settlementResultNet" -->
          <FormInputNumber
            v-model="settlementResultGross"
            name="settlementResultGross"
            :isDirty="handleDirtyState('settlementResultGross')"
            :initialValue="editData.settlementResultGross"
            :disabled="isDisabled('settlementResultGross')"
            formatType="currency"
            :currency="currency"
          />
          <!-- :suggestions="props.suggestions?.settlementResultGross" -->
        </div>

        <DetailFormfooter
          v-if="!isNew"
          :isNew="isNew"
          :editMode="editMode"
          :isAnyFieldDirty="isAnyFieldDirty"
          :onGoToList="goToList"
          :onToggleEditMode="toggleEditMode"
          :onSubmit="submitForm"
          :onConfirmResetChanges="confirmResetChanges"
        />
      </SectionWrapper>

      <div v-if="!isNew">
        <h3 class="pt-8">{{ t('text.tapOverview') }}</h3>
        <SectionWrapper>
          <TapOverview
            :costsNet="costsNet"
            :statedCustomerPrepaymentNet="statedCustomerPrepaymentNet"
            :settlementResultNet="settlementResultNet"
            :customerPrepaymentGross="customerPrepaymentGross"
            :lineItems="editData.lineItems ?? []"
            :currency="currency"
          />
        </SectionWrapper>
      </div>

      <div v-if="!isNew">
        <h3 class="pt-8">{{ t('text.invoiceLineItems') }}</h3>
        <SectionWrapper>
          <OperatingCostInvoiceLines
            :invoiceId="id"
            :isNew="isNew"
            :editMode="editMode"
            :existingOperatingCostInvoiceLines="editData.lineItems ?? []"
            :onUpdateLineItemsOnCreate="
              (data: OperatingCostInvoiceLineItemSimple[]) => (lineItemsToCreate = data)
            "
            :fetchData="fetchData"
            :latestInvoiceId="latestInvoiceIdForLines"
            :latestInvoiceLineItems="latestInvoiceLineItemsForLines"
            :importSourceInvoices="props.importSourceInvoices"
            :currentContractId="effectiveContractId"
            :currency="currency"
            :daysInBillingPeriod="daysInBillingPeriod"
          />
        </SectionWrapper>
      </div>

      <DetailFormfooter
        v-if="isNew"
        :isNew="isNew"
        :editMode="editMode"
        :isAnyFieldDirty="isAnyFieldDirty"
        :onGoToList="goToList"
        :onToggleEditMode="toggleEditMode"
        :onSubmit="submitForm"
        :onConfirmResetChanges="confirmResetChanges"
      />
    </form>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useField, useForm } from 'vee-validate';
import { useI18n } from 'vue-i18n';

import type {
  DetailFormDisableProps,
  DetailFormExtraProps,
} from '@/components/details/detailTypes';
import {
  DetailNavigationInlinePropsDefaults,
  useDetailNavigation,
  type UseDetailNavigationInlineProps,
} from '@/components/details/hooks/useDetailNavigation';
import { useFormDirtyState } from '@/components/forms/hooks/useFormDirtyState';
import { useFieldDisabling } from '@/components/forms/hooks/useFormFieldDisable';
import { useFormPrefill } from '@/components/forms/hooks/useFormPrefill';
import type {
  LazySelectFetchOptions,
  PaginatedResponse,
} from '@/components/forms/hooks/useLazySelect.ts';
import LinkedTicketTag from '@/components/LinkedTicketTag.vue';
import SectionWrapper from '@/components/section-wrapper/SectionWrapper.vue';
import { useContracts } from '@/views/contracts/hooks/useContracts';
import OperatingCostInvoiceLines from '@/views/operatingcostinvoices/components/operationcostinvoicelines/OperatingCostInvoiceLines.vue';
import type { OperatingCostInvoiceLineItemSimple } from '@/views/operatingcostinvoices/components/operationcostinvoicelines/operatingCostInvoiceTypes';
import { getOperatingCostInvoiceSchema } from '@/views/operatingcostinvoices/configs/operatingCostInvoiceFormConfig';
import {
  useOperatingCostInvoices,
  useOperatingCostInvoicesConverter,
} from '@/views/operatingcostinvoices/hooks/useOperatingCostInvoices';
import type { OperatingCostInvoiceSimple } from '@/views/operatingcostinvoices/operatingCostInvoiceTypes';
import { usePointOfSales } from '@/views/pointofsales/hooks/usePointOfSales';
import type { TicketEnriched } from '@/views/tickets/ticketsTypes';
import { useDetailView } from '@/composables/useDetailView';
import {
  type ContractSimpleV2Dto,
  type ContractV1Dto,
  OperatingCostInvoiceV1Dto,
  OrderByRelationalPropertyDto,
  type PointOfSaleSimpleV2Dto,
  type PointOfSaleV1Dto,
} from '@/openapi';

import TapOverview from './components/operationcostinvoicelines/tap-overview/TapOverview.vue';

const props = withDefaults(
  defineProps<
    UseDetailNavigationInlineProps &
      DetailFormExtraProps<OperatingCostInvoiceSimple> &
      DetailFormDisableProps<OperatingCostInvoiceSimple> & {
        ticket?: TicketEnriched | null;
        contracts?: ContractSimpleV2Dto[];
        latestInvoiceId?: string | null;
        isNew?: boolean;
        latestInvoiceLineItems?: OperatingCostInvoiceLineItemSimple[];
        isSubComponent?: boolean;
        importSourceInvoices?: OperatingCostInvoiceSimple[];
      }
  >(),
  {
    ...DetailNavigationInlinePropsDefaults,
    initialData: undefined,
    placeholders: undefined,
    allDisabled: undefined,
    disabledFields: undefined,
    suggestions: undefined,
    ticket: undefined,
    contracts: undefined,
    isNew: undefined,
    isEditMode: undefined,
    latestInvoiceId: undefined,
    latestInvoiceLineItems: undefined,
    isSubComponent: undefined,
    importSourceInvoices: undefined,
  },
);

const emit = defineEmits<{
  (
    e: 'submitSuccess',
    payload?: {
      id?: string;
      request?: OperatingCostInvoiceSimple;
      resp?: OperatingCostInvoiceSimple;
    },
  ): void;
  (e: 'contractIdChange', contractId: string | null): void;
  (e: 'requestToggleEditMode', next: boolean): void;
  (e: 'requestGoToList'): void;
  (e: 'requestGoToSubcomponentList'): void;
}>();

const { t } = useI18n();
const inModal = computed(() => props.inModal ?? false);

const { fetchPointOfSalesSimple, fetchPointOfSale } = usePointOfSales();
const {
  fetchOperatingCostInvoice,
  createOperatingCostInvoice,
  updateOperatingCostInvoice,
  deleteOperatingCostInvoice,
} = useOperatingCostInvoices();
const {
  convertOperatingCostInvoiceDtoToOperatingCostInvoiceSimple,
  convertOperatingCostInvoiceSimpleToCreateOperatingCostInvoiceDto,
  convertOperatingCostInvoiceSimpleToUpdateOperatingCostInvoiceDto,
  fetchLatestByContractAndConvertToSimple,
} = useOperatingCostInvoicesConverter();

const { id, isNew, editMode, mainComponentID, goToList, toggleEditMode } = useDetailNavigation({
  isSubComponent: computed(() => props.isSubComponent ?? false).value,
  uuidFromProp: computed(() => props.uuid),
  isNewFromProp: computed(() => props.isNew),
  editModeFromProp: computed(() => props.editMode),
});

const { isAnyFieldDirty, handleDirtyState } = useFormDirtyState();
// For existing invoices, always show fields even if contract is temporarily cleared
// For new invoices, only show fields after a contract is selected
const hasSelectedContract = computed(() => {
  // If editing an existing invoice, always show fields
  if (!isNew.value) return true;
  // For new invoices, only show fields when a contract is selected
  return !!contractId.value;
});

const operatingCostInvoice = ref<OperatingCostInvoiceSimple>();
const editData = ref<Partial<OperatingCostInvoiceSimple>>({});
const lineItemsToCreate = ref<OperatingCostInvoiceLineItemSimple[]>([]);

const { fetchContractsSimplePaginated, fetchContract } = useContracts();

const latestInvoiceIdLocal = ref<string | null>(props.latestInvoiceId ?? null);
const latestInvoiceLineItemsLocal = ref<OperatingCostInvoiceLineItemSimple[]>(
  props.latestInvoiceLineItems ?? [],
);

const effectiveContractId = computed<string | null>(() => {
  return contractId.value || editData.value.contractId || props.initialData?.contractId || null;
});

const latestInvoiceIdForLines = computed(() => {
  return props.latestInvoiceId ?? latestInvoiceIdLocal.value ?? undefined;
});

const latestInvoiceLineItemsForLines = computed(() => {
  return props.latestInvoiceLineItems ?? latestInvoiceLineItemsLocal.value ?? [];
});

const daysInBillingPeriod = computed<number | undefined>(() => {
  // Only calculate for existing invoices, not for new ones
  if (isNew.value || !periodStart.value || !periodEnd.value) return;

  const startDate = new Date(periodStart.value);
  const endDate = new Date(periodEnd.value);

  // Use UTC midnight values to avoid timezone/DST edge cases.
  const startUtc = Date.UTC(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
  const endUtc = Date.UTC(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
  const diffDays = Math.floor((endUtc - startUtc) / (1000 * 60 * 60 * 24));

  // Inclusive day count: both period start and period end are counted.
  return diffDays + 1;
});

const { handleSubmit, setValues, resetForm } = useForm<OperatingCostInvoiceSimple>({
  validationSchema: getOperatingCostInvoiceSchema(t),
  validateOnMount: false,
});

const { watchInitialData } = useFormPrefill<OperatingCostInvoiceSimple>({
  initialData: computed(() => props.initialData ?? undefined),
  setValues,
  resetForm: (args) => resetForm(args),
  isNew,
  isDirty: () => isAnyFieldDirty.value,
  forceOnChange: true,
});

const { isDisabled } = useFieldDisabling<OperatingCostInvoiceSimple>({
  editMode,
  allDisabled: computed(() => props.allDisabled),
  disabledFields: computed(() => props.disabledFields),
});

const submitForm = handleSubmit(onSubmit);

const { value: contractId } = useField<string>('contractId');
const { value: pointOfSaleId } = useField<string>('pointOfSaleId');
const { value: issuerLegalName } = useField<string>('issuerLegalName');
const { value: issueDate } = useField<string | null>('issueDate');
const { value: periodStart } = useField<string | null>('periodStart');
const { value: periodEnd } = useField<string | null>('periodEnd');

const { value: documentReviewDeadline } = useField<string | undefined>('documentReviewDeadline');
const { value: objectionDeadline } = useField<string | undefined>('objectionDeadline');
const { value: internalObjectionDeadline } = useField<string | null>('internalObjectionDeadline');
const { value: isHeatingCostsCompliantHKVO } = useField<boolean>('isHeatingCostsCompliantHKVO');
const { value: costsNet } = useField<number | undefined>('costsNet');
const { value: vatRate } = useField<number>('vatRate');
const { value: customerPrepaymentNet } = useField<number | undefined>('customerPrepaymentNet');
const { value: customerPrepaymentGross } = useField<number | undefined>('customerPrepaymentGross');
const { value: currency } = useField<OperatingCostInvoiceV1Dto.currency>('currency');
const { value: statedCustomerPrepaymentNet } = useField<number | undefined>(
  'statedCustomerPrepaymentNet',
);
const { value: settlementResultNet } = useField<number | undefined>('settlementResultNet');
const { value: settlementResultGross } = useField<number | undefined>('settlementResultGross');

function confirmResetChanges() {
  setValues(editData.value);
}

async function confirmDelete() {
  await deleteOperatingCostInvoice(id.value);
  goToList();
}

async function fetchSimpleContracts(
  options: LazySelectFetchOptions,
): Promise<PaginatedResponse<ContractSimpleV2Dto> | undefined> {
  const result = await fetchContractsSimplePaginated({
    searchString: options.searchString ?? '',
    page: options.page,
    limit: options.limit,
  });

  return result;
}

async function fetchContractDetails(id: string): Promise<ContractV1Dto | null | undefined> {
  try {
    // Validate that we have a valid ID before fetching
    if (!id || typeof id !== 'string' || id.trim() === '') {
      return null;
    }
    return await fetchContract(id);
  } catch (err) {
    console.error('Error fetching contract details for ID:', id, err);
    return null;
  }
}

async function fetchSimplePointOfSales(
  options: LazySelectFetchOptions,
): Promise<PaginatedResponse<PointOfSaleSimpleV2Dto> | undefined> {
  try {
    // Use the contractId parameter directly in the API call
    const result = await fetchPointOfSalesSimple(
      [{ propertyName: 'name', value: OrderByRelationalPropertyDto.value.ASC }],
      undefined,
      options.searchString ?? undefined,
      options.page,
      options.limit,
      contractId.value ?? undefined,
    );

    return result;
  } catch (err) {
    console.error('Error fetching point of sales:', err);
    // Return empty response structure on error
    return { data: [], count: 0, pages: 0, page: options.page, limit: options.limit };
  }
}

async function fetchPointOfSaleDetails(id: string): Promise<PointOfSaleV1Dto | null | undefined> {
  try {
    // Validate that we have a valid ID before fetching
    if (!id || typeof id !== 'string' || id.trim() === '') {
      return null;
    }
    return await fetchPointOfSale(id);
  } catch (err) {
    console.error('Error fetching point of sale details for ID:', id, err);
    return null;
  }
}

async function onSubmit(values: OperatingCostInvoiceSimple) {
  if (!isNew.value && id.value) {
    const updateDto = convertOperatingCostInvoiceSimpleToUpdateOperatingCostInvoiceDto(values);

    // Add ticketId if available from props
    if (props.ticket?.id) {
      updateDto.ticketId = props.ticket.id;
    }

    await updateOperatingCostInvoice(String(id.value), updateDto);
    toggleEditMode();
    await fetchData();
  } else {
    const createDto = convertOperatingCostInvoiceSimpleToCreateOperatingCostInvoiceDto(
      values,
      lineItemsToCreate.value,
    );

    // Add ticketId if available from props
    if (props.ticket?.id) {
      createDto.ticketId = props.ticket.id;
    }
    const resp = await createOperatingCostInvoice(createDto);

    emit('submitSuccess', {
      id: resp.id,
      request: values,
      resp: convertOperatingCostInvoiceDtoToOperatingCostInvoiceSimple(resp),
    });

    goToList();
  }
}

/** placeholder helper */
const ph = (key: keyof OperatingCostInvoiceSimple, fallback?: string) =>
  props.placeholders?.[key] ?? fallback ?? '';

const fetchData = async () => {
  // 1) Prefill for NEW records - completely blocks fetchData
  if (isNew.value && editData.value && Object.keys(editData.value).length > 0) {
    // Prefill bereits angewendet → NICHT fetchData aufrufen
    return;
  }

  //2) Without ID → no fetch
  if (!id.value) return;

  // 3) Normal fetch for existing records
  try {
    const data = await fetchOperatingCostInvoice(String(id.value));
    const simple = data && convertOperatingCostInvoiceDtoToOperatingCostInvoiceSimple(data);

    operatingCostInvoice.value = simple || undefined;
    editData.value = { ...(simple || {}) };

    resetForm({ values: { ...simple } });
  } catch (err) {
    console.error('Error fetching operating cost invoice:', err);
    // Reset to empty state on error
    operatingCostInvoice.value = undefined;
    editData.value = {};
  }
};

const clearValues = () => {
  operatingCostInvoice.value = undefined;
  editData.value = {};
};

const { componentKey } = useDetailView({
  fetchData,
  clearValues,
  isSubComponent: computed(() => props.isSubComponent ?? false).value,
});

watchInitialData((merged) => {
  // Keep local state in sync
  // This should only run for NEW invoices due to the isNew check in useFormPrefill
  // For existing invoices being edited, this won't run to preserve user changes
  operatingCostInvoice.value = merged;
  editData.value = { ...merged };
});

watch(
  contractId,
  async (newContractId, oldContractId) => {
    emit('contractIdChange', newContractId || null);

    // Only process if this is an actual change, not the initial load
    const isActualChange = oldContractId !== undefined && newContractId !== oldContractId;

    if (isActualChange) {
      // Update editData with the new contractId to ensure it's in sync
      if (editData.value) {
        editData.value.contractId = newContractId || '';
      }

      // Clear point of sale since it's contract-dependent
      // Clear both the field value AND editData to ensure the FormLazySelect is fully reset
      pointOfSaleId.value = '';
      if (editData.value) {
        editData.value.pointOfSaleId = '';
      }

      // For existing invoices, we want to preserve all other field values
      // The form dirty state will track that the user has made changes
    }

    // When contract is cleared completely
    if (!newContractId) {
      latestInvoiceIdLocal.value = null;
      latestInvoiceLineItemsLocal.value = [];
      return;
    }

    // For existing invoices, DON'T fetch latest invoice data or reset anything
    // The user is editing an existing invoice and changing the contract
    // We should preserve all their other edits
    if (!isNew.value) {
      return;
    }

    // Only for NEW invoices: fetch the latest invoice for this contract
    // to help pre-fill line items
    const latest = await fetchLatestByContractAndConvertToSimple(newContractId);

    if (!latest) {
      latestInvoiceIdLocal.value = null;
      latestInvoiceLineItemsLocal.value = [];
      return;
    }

    latestInvoiceIdLocal.value = latest.id ?? null;
    latestInvoiceLineItemsLocal.value = latest.lineItems ?? [];
  },
  { immediate: true },
);

onMounted(() => {
  // 1) Prefer contractId from initialData (e.g. wrapper/template logic)
  if (props.initialData?.contractId) {
    contractId.value = String(props.initialData.contractId);
    return;
  }

  // 2) If there is exactly one contract via props, use it
  if (props.contracts && props.contracts.length === 1 && props.contracts[0]) {
    contractId.value = String(props.contracts[0].id);
    return;
  }

  // 3) If we are ACTUALLY in contract subcomponent context (isSubComponent=true),
  //    use mainComponentID from route as the contractId
  if (props.isSubComponent && mainComponentID.value) {
    contractId.value = String(mainComponentID.value);
    return;
  }

  // 4) Otherwise: contractId will be loaded from the invoice data in fetchData
  //    or user has to select manually for new invoices
});
</script>
